import { Badge, Button, Card, Group, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconDeviceMobile, IconPlus, IconTerminal2 } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/**
 * Step 1 of the corridor: how the vendor wants to connect WhatsApp.
 *
 * The whole point is the question being asked. Today the page offers
 * "Embedded Signup" and "Coexistence" as equal-weight cards — Meta's internal
 * product names, which mean nothing to a shop owner. The user's actual question
 * is "do I already use WhatsApp Business on my phone?", so that is what we ask,
 * and the answer picks the mechanism for them.
 *
 * Ordering is deliberate. When coexistence is available it goes first: most
 * small businesses already run WhatsApp Business on a phone, and "keep your
 * number and your chats" is the strongest sentence we have. The manual path is
 * third and visually subordinate — a plain row, no filled button — because it
 * is right for a small minority and confusing for everyone else.
 */
export interface SituationChoiceProps {
  coexistenceAvailable: boolean;
  embeddedSignupAvailable: boolean;
  manualAvailable: boolean;
  /** True while a Meta popup is open, so both primary buttons disable together. */
  busy?: boolean;
  onKeepMyNumber: () => void;
  onNewNumber: () => void;
  onManual: () => void;
}

function ChoiceCard({
  icon,
  title,
  description,
  action,
  badge,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action: ReactNode;
  badge?: string;
}) {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm" justify="space-between" h="100%">
        <Stack gap="xs">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size={36} radius="md" variant="light">
              {icon}
            </ThemeIcon>
            <Group gap={6}>
              <Text fw={600}>{title}</Text>
              {badge && (
                <Badge size="sm" variant="light">
                  {badge}
                </Badge>
              )}
            </Group>
          </Group>
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        </Stack>
        {action}
      </Stack>
    </Card>
  );
}

export function SituationChoice({
  coexistenceAvailable,
  embeddedSignupAvailable,
  manualAvailable,
  busy,
  onKeepMyNumber,
  onNewNumber,
  onManual,
}: SituationChoiceProps) {
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: coexistenceAvailable ? 2 : 1 }} spacing="md">
        {coexistenceAvailable && (
          <ChoiceCard
            icon={<IconDeviceMobile size={20} />}
            title="Keep the number I already use"
            badge="Most popular"
            description="You'll keep using the app on your phone and message from here too. Same number, same chats."
            action={
              <Button onClick={onKeepMyNumber} loading={busy} fullWidth>
                Keep my number
              </Button>
            }
          />
        )}

        {embeddedSignupAvailable && (
          <ChoiceCard
            icon={<IconPlus size={20} />}
            title="Set me up with a new number"
            description="Takes about two minutes. We handle the setup with Meta for you."
            action={
              <Button
                onClick={onNewNumber}
                loading={busy}
                fullWidth
                variant={coexistenceAvailable ? 'light' : 'filled'}
              >
                Connect WhatsApp
              </Button>
            }
          />
        )}
      </SimpleGrid>

      {/* Third option, deliberately subordinate: a plain row rather than a
          peer card, so it reads as "if none of the above describes you". */}
      {manualAvailable && (
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size={28} radius="md" variant="subtle" color="gray">
              <IconTerminal2 size={16} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                I already have WhatsApp Cloud API access
              </Text>
              <Text size="xs" c="dimmed">
                For developers and agencies with their own Meta app.
              </Text>
            </Stack>
          </Group>
          <Button variant="subtle" size="compact-sm" onClick={onManual}>
            Set up
          </Button>
        </Group>
      )}
    </Stack>
  );
}
